// src/components/jobs/timeline/JobEventTimeline.jsx
// V26: Job event timeline visualization component

import { useState, useEffect } from 'react';
import { useGraphQL } from '../../../graphql';
import {
    CheckCircleIcon,
    XCircleIcon,
    PlayIcon,
    ClockIcon,
    ArrowPathIcon,
    StopIcon,
    ExclamationTriangleIcon,
    HeartIcon,
    ChartBarIcon,
} from '@heroicons/react/24/outline';

const EVENT_ICONS = {
    CREATED: ClockIcon,
    STARTED: PlayIcon,
    PROGRESS: ChartBarIcon,
    COMPLETED: CheckCircleIcon,
    FAILED: XCircleIcon,
    RETRYING: ArrowPathIcon,
    CANCELLED: StopIcon,
    TIMEOUT: ExclamationTriangleIcon,
    HEARTBEAT: HeartIcon,
};

const EVENT_COLORS = {
    CREATED: 'text-gray-400 bg-gray-700',
    STARTED: 'text-blue-700 bg-blue-50',
    PROGRESS: 'text-purple-700 bg-purple-50',
    COMPLETED: 'text-green-700 bg-green-50',
    FAILED: 'text-red-700 bg-red-50',
    RETRYING: 'text-yellow-700 bg-yellow-50',
    CANCELLED: 'text-orange-700 bg-orange-50',
    TIMEOUT: 'text-red-700 bg-red-50',
    HEARTBEAT: 'text-pink-700 bg-pink-50',
};

const EVENT_LABELS = {
    CREATED: 'Job Created',
    STARTED: 'Execution Started',
    PROGRESS: 'Progress Update',
    COMPLETED: 'Completed Successfully',
    FAILED: 'Failed',
    RETRYING: 'Retrying',
    CANCELLED: 'Cancelled',
    TIMEOUT: 'Timed Out',
    HEARTBEAT: 'Heartbeat',
};

const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
};

const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
};

const EventCard = ({ event, isLast }) => {
    const [showDetails, setShowDetails] = useState(false);
    const Icon = EVENT_ICONS[event.eventType] || ClockIcon;
    const colorClass = EVENT_COLORS[event.eventType] || 'text-gray-400 bg-gray-700';
    const label = EVENT_LABELS[event.eventType] || event.eventType;

    const hasDetails = event.details && event.details !== '{}' && event.details !== 'null';

    return (
        <div className="relative flex items-start">
            {/* Timeline connector */}
            {!isLast && (
                <div className="absolute left-4 top-8 w-0.5 h-full bg-gray-700" />
            )}

            {/* Icon -  like Temporal dots */}
            <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center ${colorClass}`}>
                <Icon className="h-4 w-4" />
            </div>

            {/* Content */}
            <div className="ml-4 flex-1 pb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <span className="font-mono text-sm font-medium text-white">{label}</span>
                        {event.progressPercent !== null && event.progressPercent !== undefined && (
                            <span className="ml-2 font-mono text-xs text-purple-700">
                                {event.progressPercent}%
                            </span>
                        )}
                    </div>
                    <span className="font-mono text-xs text-gray-500" title={formatTimestamp(event.timestamp)}>
                        {formatRelativeTime(event.timestamp)}
                    </span>
                </div>

                {event.progressMessage && (
                    <p className="mt-1 font-mono text-xs text-gray-400">{event.progressMessage}</p>
                )}

                {hasDetails && (
                    <div className="mt-2">
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="font-mono text-xs text-primary hover:text-primary/80"
                        >
                            {showDetails ? 'Hide details' : 'Show details'}
                        </button>
                        {showDetails && (
                            <pre className="mt-2 p-2 bg-gray-800 font-mono text-xs text-gray-300 overflow-x-auto">
                                {JSON.stringify(JSON.parse(event.details), null, 2)}
                            </pre>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const ProgressBar = ({ percent, message }) => {
    return (
        <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
                <span className="font-mono text-xs text-gray-400 uppercase">Progress</span>
                <span className="font-mono text-xs font-bold text-white">{percent}%</span>
            </div>
            <div className="w-full bg-gray-700 h-1.5">
                <div
                    className="bg-primary h-1.5 transition-all duration-300"
                    style={{ width: `${percent}%` }}
                />
            </div>
            {message && (
                <p className="mt-1 font-mono text-xs text-gray-400">{message}</p>
            )}
        </div>
    );
};

const JobEventTimeline = ({ jobId, autoRefresh = false }) => {
    const { executeQuery, queries, loading } = useGraphQL();
    const [timeline, setTimeline] = useState(null);
    const [error, setError] = useState(null);

    const loadTimeline = async () => {
        try {
            const data = await executeQuery(queries.getJobTimeline(), { id: jobId });
            setTimeline(data.jobTimeline);
            setError(null);
        } catch (err) {
            console.error('Failed to load job timeline:', err);
            setError(err.message);
        }
    };

    useEffect(() => {
        if (jobId) {
            loadTimeline();
        }
    }, [jobId]);

    useEffect(() => {
        if (!autoRefresh || !jobId) return;

        const interval = setInterval(() => {
            loadTimeline();
        }, 3000);

        return () => clearInterval(interval);
    }, [autoRefresh, jobId]);

    if (loading && !timeline) {
        return (
            <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin" />
                <span className="ml-2 text-gray-400">Loading timeline...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 p-4">
                <p className="font-mono text-xs text-red-700">Failed to load timeline: {error}</p>
            </div>
        );
    }

    if (!timeline) {
        return (
            <div className="text-gray-400 text-center py-8">
                No timeline data available
            </div>
        );
    }

    const { job, events, totalDuration, currentProgress } = timeline;
    const isRunning = job.status === 'RUNNING';

    return (
        <div className="space-y-4">
            {/* Header with stats */}
            <div className="flex items-center justify-between bg-gray-800 border border-gray-700 p-3">
                <div>
                    <h4 className="font-mono text-xs font-bold text-white uppercase">Event Timeline</h4>
                    <p className="font-mono text-xs text-gray-500">
                        {events.length} events
                        {totalDuration && ` · ${totalDuration}`}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {job.retryCount > 0 && (
                        <div className="text-center">
                            <span className="font-mono text-xs font-bold text-yellow-700">{job.retryCount}</span>
                            <span className="font-mono text-xs text-gray-500 block">retries</span>
                        </div>
                    )}
                    {job.cancelRequested && (
                        <span className="px-2 py-0.5 bg-orange-50 text-orange-700 font-mono text-xs font-bold">
                            CANCEL REQUESTED
                        </span>
                    )}
                    {isRunning && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 font-mono text-xs font-bold animate-pulse">
                            RUNNING
                        </span>
                    )}
                </div>
            </div>

            {/* Progress bar for running jobs */}
            {isRunning && currentProgress !== null && (
                <ProgressBar
                    percent={currentProgress}
                    message={job.progressMessage}
                />
            )}

            {/* Retry & timeout info */}
            {(job.maxRetries > 0 || job.timeoutSeconds) && (
                <div className="font-mono text-xs text-gray-500 bg-gray-800 border border-gray-700 p-2 flex items-center gap-4">
                    {job.maxRetries > 0 && (
                        <span>Retries: <span className="text-gray-300">{job.retryCount}/{job.maxRetries}</span></span>
                    )}
                    {job.timeoutSeconds && (
                        <span>Timeout: <span className="text-gray-300">{job.timeoutSeconds}s</span></span>
                    )}
                    {job.retryBackoffSeconds > 0 && (
                        <span>Backoff: <span className="text-gray-300">{job.retryBackoffSeconds}s</span></span>
                    )}
                </div>
            )}

            {/* Event list */}
            <div className="space-y-0">
                {events.map((event, index) => (
                    <EventCard
                        key={event.id}
                        event={event}
                        isLast={index === events.length - 1}
                    />
                ))}
            </div>

            {events.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    No events recorded yet
                </div>
            )}
        </div>
    );
};

export default JobEventTimeline;
